import { useEffect, useState } from 'react';
import { browserSupportsWebAuthn, startAuthentication, startRegistration } from '@simplewebauthn/browser';
import QRCode from 'qrcode';
import Admin from './Admin';
import Logo from './Logo';
import './Acceso.css';

export const rutasAcceso = ['/registro', '/ingresar', '/mi-cuenta', '/recuperar', '/restablecer', '/admin'];

export async function api(ruta, body, method = body ? 'POST' : 'GET') {
  const res = await fetch(ruta, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Algo salió mal. Intenta de nuevo.');
  return data;
}

export function calcularEdad(fecha) {
  if (!fecha) return '';
  const nac = new Date(`${fecha}T00:00:00`);
  const hoy = new Date();
  let edad = hoy.getFullYear() - nac.getFullYear();
  const cumplioEsteAno =
    hoy.getMonth() > nac.getMonth() ||
    (hoy.getMonth() === nac.getMonth() && hoy.getDate() >= nac.getDate());
  if (!cumplioEsteAno) edad--;
  return edad >= 0 ? edad : '';
}

export function Campo({ label, ...props }) {
  return (
    <label className="acceso__campo">
      <span>{label}</span>
      <input {...props} />
    </label>
  );
}

function Registro() {
  const [codigo, setCodigo] = useState('');
  const [atleta, setAtleta] = useState(null);
  const [datos, setDatos] = useState({
    nombreAcudiente: '',
    documento: '',
    fechaNacimiento: '',
    direccion: '',
    password: '',
    confirmar: '',
  });
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  const cambiar = (campo) => (e) => setDatos({ ...datos, [campo]: e.target.value });

  const verificarCodigo = async (e) => {
    e.preventDefault();
    setError('');
    setEnviando(true);
    try {
      setAtleta(await api(`/api/codigo/${encodeURIComponent(codigo.trim())}`));
    } catch (err) {
      setError(err.message);
    } finally {
      setEnviando(false);
    }
  };

  const registrar = async (e) => {
    e.preventDefault();
    setError('');
    if (datos.password !== datos.confirmar) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setEnviando(true);
    try {
      const { confirmar, ...resto } = datos;
      await api('/api/registro', { codigo, ...resto });
      window.location.href = '/mi-cuenta';
    } catch (err) {
      setError(err.message);
      setEnviando(false);
    }
  };

  if (!atleta) {
    return (
      <form className="acceso__form" onSubmit={verificarCodigo}>
        <h1 className="acceso__titulo">Registro</h1>
        <p className="acceso__lead">Ingresa el código del estudiante que te entregó el Sensei.</p>
        <Campo
          label="Código del estudiante"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          placeholder="KM-XXXXXX"
          autoCapitalize="characters"
          autoComplete="username"
          required
        />
        {error && <p className="acceso__error">{error}</p>}
        <button className="btn btn--primary acceso__btn" disabled={enviando}>
          Continuar
        </button>
        <p className="acceso__alt">
          ¿Ya te registraste? <a href="/ingresar">Inicia sesión</a>
        </p>
      </form>
    );
  }

  if (atleta.registrado) {
    return (
      <div className="acceso__form">
        <h1 className="acceso__titulo">Ya registrado</h1>
        <p className="acceso__lead">
          El código de <strong>{atleta.nombreAtleta}</strong> ya fue registrado.
        </p>
        <a href="/ingresar" className="btn btn--primary acceso__btn">
          Iniciar sesión
        </a>
      </div>
    );
  }

  return (
    <form className="acceso__form" onSubmit={registrar}>
      <h1 className="acceso__titulo">Registro</h1>
      <div className="acceso__atleta">
        <span>Atleta</span>
        <strong>{atleta.nombreAtleta}</strong>
      </div>
      <Campo label="Nombre y apellido del papá o mamá" value={datos.nombreAcudiente} onChange={cambiar('nombreAcudiente')} required />
      <Campo label="Número de documento" value={datos.documento} onChange={cambiar('documento')} inputMode="numeric" required />
      <div className="acceso__fila">
        <Campo
          label="Fecha de nacimiento"
          type="date"
          value={datos.fechaNacimiento}
          onChange={cambiar('fechaNacimiento')}
          max={new Date().toISOString().slice(0, 10)}
          required
        />
        <Campo label="Edad" value={calcularEdad(datos.fechaNacimiento)} readOnly tabIndex={-1} />
      </div>
      <Campo label="Dirección" value={datos.direccion} onChange={cambiar('direccion')} autoComplete="street-address" required />
      <Campo label="Contraseña" type="password" value={datos.password} onChange={cambiar('password')} minLength={8} autoComplete="new-password" required />
      <Campo label="Confirmar contraseña" type="password" value={datos.confirmar} onChange={cambiar('confirmar')} minLength={8} autoComplete="new-password" required />
      {error && <p className="acceso__error">{error}</p>}
      <button className="btn btn--primary acceso__btn" disabled={enviando}>
        Registrarme
      </button>
    </form>
  );
}

function Ingresar() {
  const [codigo, setCodigo] = useState('');
  const [password, setPassword] = useState('');
  const [desafio2fa, setDesafio2fa] = useState(null);
  const [codigo2fa, setCodigo2fa] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  // Shared by the three ways in: password, A2F code and passkey.
  const intentar = async (accion) => {
    setError('');
    setEnviando(true);
    try {
      const res = await accion();
      if (res?.requiere2fa) {
        setDesafio2fa(res.desafio);
        setEnviando(false);
        return;
      }
      window.location.href = '/mi-cuenta';
    } catch (err) {
      setError(err.message);
      setEnviando(false);
    }
  };

  const ingresar = (e) => {
    e.preventDefault();
    intentar(() => api('/api/ingresar', { codigo, password }));
  };

  const verificar2fa = (e) => {
    e.preventDefault();
    intentar(() => api('/api/ingresar/2fa', { desafio: desafio2fa, codigo2fa }));
  };

  const ingresarConLlave = () =>
    intentar(async () => {
      const { desafio, opciones } = await api('/api/llaves/opciones-ingreso', {});
      let respuesta;
      try {
        respuesta = await startAuthentication({ optionsJSON: opciones });
      } catch {
        throw new Error('No se usó ninguna llave de acceso.');
      }
      return api('/api/llaves/ingresar', { desafio, respuesta });
    });

  if (desafio2fa) {
    return (
      <form className="acceso__form" onSubmit={verificar2fa}>
        <h1 className="acceso__titulo">Verificación</h1>
        <p className="acceso__lead">Escribe el código de 6 dígitos de tu app autenticadora.</p>
        <Campo
          label="Código de verificación"
          value={codigo2fa}
          onChange={(e) => setCodigo2fa(e.target.value)}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          autoFocus
          required
        />
        {error && <p className="acceso__error">{error}</p>}
        <button className="btn btn--primary acceso__btn" disabled={enviando}>
          Verificar
        </button>
      </form>
    );
  }

  return (
    <form className="acceso__form" onSubmit={ingresar}>
      <h1 className="acceso__titulo">Iniciar sesión</h1>
      <Campo
        label="Código del estudiante"
        value={codigo}
        onChange={(e) => setCodigo(e.target.value)}
        placeholder="KM-XXXXXX"
        autoCapitalize="characters"
        autoComplete="username"
        required
      />
      <Campo label="Contraseña" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
      {error && <p className="acceso__error">{error}</p>}
      <button className="btn btn--primary acceso__btn" disabled={enviando}>
        Ingresar
      </button>
      {browserSupportsWebAuthn() && (
        <button type="button" className="btn btn--ghost acceso__btn" onClick={ingresarConLlave} disabled={enviando}>
          Ingresar con llave de acceso
        </button>
      )}
      <p className="acceso__alt">
        <a href="/recuperar">¿Olvidaste tu contraseña?</a>
      </p>
      <p className="acceso__alt">
        ¿Primera vez? <a href="/registro">Regístrate con tu código</a>
      </p>
    </form>
  );
}

function LlavesDeAcceso({ llaves, recargar }) {
  const [error, setError] = useState('');

  const agregar = async () => {
    setError('');
    try {
      const { desafio, opciones } = await api('/api/llaves/opciones-registro', {});
      let respuesta;
      try {
        respuesta = await startRegistration({ optionsJSON: opciones });
      } catch {
        throw new Error('No se creó la llave de acceso.');
      }
      await api('/api/llaves/registrar', { desafio, respuesta });
      recargar();
    } catch (err) {
      setError(err.message);
    }
  };

  const quitar = async (id) => {
    await api(`/api/llaves/${encodeURIComponent(id)}`, undefined, 'DELETE');
    recargar();
  };

  return (
    <section className="acceso__seccion">
      <h2>Llaves de acceso</h2>
      <p className="acceso__lead">Entra sin contraseña con la huella, el rostro o el PIN de tu celular o computador.</p>
      {llaves.map((llave) => (
        <div key={llave.id} className="acceso__llave">
          <span>Llave creada el {new Date(llave.creadaEn).toLocaleDateString('es-CO')}</span>
          <button type="button" onClick={() => quitar(llave.id)}>
            Quitar
          </button>
        </div>
      ))}
      {error && <p className="acceso__error">{error}</p>}
      {browserSupportsWebAuthn() ? (
        <button type="button" className="btn btn--ghost acceso__btn" onClick={agregar}>
          Agregar llave de acceso
        </button>
      ) : (
        <p className="acceso__lead">Este navegador no admite llaves de acceso.</p>
      )}
    </section>
  );
}

function VerificacionDosPasos({ activa, recargar }) {
  const [config, setConfig] = useState(null);
  const [codigo2fa, setCodigo2fa] = useState('');
  const [error, setError] = useState('');

  const iniciar = async () => {
    setError('');
    try {
      const datos = await api('/api/2fa/iniciar', {});
      setConfig({ ...datos, qr: await QRCode.toDataURL(datos.otpauth, { margin: 1, width: 200 }) });
    } catch (err) {
      setError(err.message);
    }
  };

  const confirmar = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (activa) await api('/api/2fa/desactivar', { codigo2fa });
      else await api('/api/2fa/activar', { desafio: config.desafio, codigo2fa });
      setConfig(null);
      setCodigo2fa('');
      recargar();
    } catch (err) {
      setError(err.message);
    }
  };

  const formCodigo = (texto) => (
    <form className="acceso__subform" onSubmit={confirmar}>
      <Campo
        label="Código de 6 dígitos"
        value={codigo2fa}
        onChange={(e) => setCodigo2fa(e.target.value)}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        required
      />
      {error && <p className="acceso__error">{error}</p>}
      <button className="btn btn--ghost acceso__btn">{texto}</button>
    </form>
  );

  return (
    <section className="acceso__seccion">
      <h2>Verificación en dos pasos</h2>
      {activa ? (
        <>
          <p className="acceso__lead">
            <strong>Activa.</strong> Al entrar con contraseña te pediremos un código de tu app autenticadora.
          </p>
          {formCodigo('Desactivar')}
        </>
      ) : config ? (
        <>
          <p className="acceso__lead">
            Escanea el código QR con Google Authenticator, Microsoft Authenticator u otra app similar, y escribe el código
            que aparece.
          </p>
          <img className="acceso__qr" src={config.qr} alt="Código QR para la app autenticadora" />
          <p className="acceso__secreto">
            ¿No puedes escanear? Clave: <code>{config.secreto}</code>
          </p>
          {formCodigo('Activar')}
        </>
      ) : (
        <>
          <p className="acceso__lead">Opcional. Protege tu cuenta con un código de tu celular además de la contraseña.</p>
          {error && <p className="acceso__error">{error}</p>}
          <button type="button" className="btn btn--ghost acceso__btn" onClick={iniciar}>
            Activar
          </button>
        </>
      )}
    </section>
  );
}

function MiCuenta() {
  const [perfil, setPerfil] = useState(null);

  const cargar = () =>
    api('/api/perfil')
      .then(setPerfil)
      .catch(() => (window.location.href = '/ingresar'));

  useEffect(() => {
    cargar();
  }, []);

  const salir = async () => {
    await api('/api/salir', {});
    window.location.href = '/ingresar';
  };

  if (!perfil) return <p className="acceso__lead">Cargando…</p>;

  const filas = [
    ['Código', perfil.codigo],
    ['Atleta', perfil.nombreAtleta],
    ['Papá o mamá', perfil.nombreAcudiente],
    ['Documento', perfil.documento],
    ['Fecha de nacimiento', perfil.fechaNacimiento],
    ['Edad', `${calcularEdad(perfil.fechaNacimiento)} años`],
    ['Dirección', perfil.direccion],
  ];

  return (
    <div className="acceso__form">
      <h1 className="acceso__titulo">Mi cuenta</h1>
      <dl className="acceso__datos">
        {filas.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
      <LlavesDeAcceso llaves={perfil.llaves} recargar={cargar} />
      <VerificacionDosPasos activa={perfil.tiene2fa} recargar={cargar} />
      <button className="btn btn--ghost acceso__btn" onClick={salir}>
        Cerrar sesión
      </button>
    </div>
  );
}

function Recuperar() {
  const esAdmin = new URLSearchParams(window.location.search).has('admin');
  const [usuario, setUsuario] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  const pedir = async (e) => {
    e.preventDefault();
    setError('');
    setEnviando(true);
    try {
      setMensaje((await api('/api/recuperar', { usuario })).mensaje);
    } catch (err) {
      setError(err.message);
    } finally {
      setEnviando(false);
    }
  };

  if (mensaje) {
    return (
      <div className="acceso__form">
        <h1 className="acceso__titulo">Revisa tu correo</h1>
        <p className="acceso__lead">{mensaje}</p>
        <a href={esAdmin ? '/admin' : '/ingresar'} className="btn btn--ghost acceso__btn">
          Volver
        </a>
      </div>
    );
  }

  return (
    <form className="acceso__form" onSubmit={pedir}>
      <h1 className="acceso__titulo">Recuperar contraseña</h1>
      <p className="acceso__lead">
        {esAdmin
          ? 'Escribe el correo del administrador y te enviaremos un enlace.'
          : 'Escribe el código del estudiante. Te enviaremos un enlace al correo que el dojo tiene registrado.'}
      </p>
      {esAdmin ? (
        <Campo label="Correo" type="email" value={usuario} onChange={(e) => setUsuario(e.target.value)} autoComplete="username" required />
      ) : (
        <Campo
          label="Código del estudiante"
          value={usuario}
          onChange={(e) => setUsuario(e.target.value)}
          placeholder="KM-XXXXXX"
          autoCapitalize="characters"
          autoComplete="username"
          required
        />
      )}
      {error && <p className="acceso__error">{error}</p>}
      <button className="btn btn--primary acceso__btn" disabled={enviando}>
        Enviar enlace
      </button>
      {!esAdmin && (
        <p className="acceso__alt">¿No tienes acceso a ese correo? Escríbele al Sensei para que te envíe un enlace.</p>
      )}
    </form>
  );
}

function Restablecer() {
  const token = new URLSearchParams(window.location.search).get('t') || '';
  const [cuenta, setCuenta] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    api(`/api/restablecer/${encodeURIComponent(token)}`)
      .then(setCuenta)
      .catch((err) => setError(err.message));
  }, []);

  const guardar = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirmar) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setEnviando(true);
    try {
      const { admin } = await api('/api/restablecer', { token, password });
      window.location.href = admin ? '/admin' : '/ingresar';
    } catch (err) {
      setError(err.message);
      setEnviando(false);
    }
  };

  if (!cuenta) {
    return (
      <div className="acceso__form">
        <h1 className="acceso__titulo">Nueva contraseña</h1>
        {error ? (
          <>
            <p className="acceso__error">{error}</p>
            <a href="/recuperar" className="btn btn--ghost acceso__btn">
              Pedir otro enlace
            </a>
          </>
        ) : (
          <p className="acceso__lead">Cargando…</p>
        )}
      </div>
    );
  }

  return (
    <form className="acceso__form" onSubmit={guardar}>
      <h1 className="acceso__titulo">Nueva contraseña</h1>
      <div className="acceso__atleta">
        <span>{cuenta.admin ? 'Administrador' : 'Atleta'}</span>
        <strong>{cuenta.cuenta}</strong>
      </div>
      <Campo label="Contraseña nueva" type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={8} autoComplete="new-password" required />
      <Campo label="Confirmar contraseña" type="password" value={confirmar} onChange={(e) => setConfirmar(e.target.value)} minLength={8} autoComplete="new-password" required />
      {error && <p className="acceso__error">{error}</p>}
      <button className="btn btn--primary acceso__btn" disabled={enviando}>
        Guardar contraseña
      </button>
    </form>
  );
}

export default function Acceso({ ruta }) {
  return (
    <main className={`acceso${ruta === '/admin' ? ' acceso--ancho' : ''}`}>
      <a href="/" className="acceso__marca">
        <Logo size={40} />
        <span>KAMAE</span>
      </a>
      {ruta === '/registro' && <Registro />}
      {ruta === '/ingresar' && <Ingresar />}
      {ruta === '/mi-cuenta' && <MiCuenta />}
      {ruta === '/recuperar' && <Recuperar />}
      {ruta === '/restablecer' && <Restablecer />}
      {ruta === '/admin' && <Admin />}
    </main>
  );
}
